import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header';
import { useLocation, useNavigate } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VolSurnameGroup = () => {
    // --- State ---
    const [surnameGroups, setSurnameGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    const location = useLocation();
    const navigate = useNavigate();

    
    const user_name = location.state?.userName;



    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: (user_name ? user_name : "Volunteer")+" Surname Groups",
            filterGroups: "Search by group name...",
            loadingData: "Loading Surname Groups...",
            noData: "No surname groups found.",
            groupName: "Group Name",
            voterCount: "Total Voters :",
            groupId: "Group ID",
            totalGroups: "Total Groups",
            totalVoters: "Total Voters",
            edit: "Edit",
            delete: "Delete",
            selectAll: "Select All",
            removeAll: "Remove All",
            updateGroup: "Update Group",
            groupNameLabel: "Group Name",
            updateGroupName: "Update Group Name",
            cancel: "Cancel",
            update: "Update"
        },
        hi: {
            pageTitle: "मेरे उपनाम समूह",
            filterGroups: "समूह नाम से खोजें...",
            loadingData: "उपनाम समूह लोड हो रहे हैं...",
            noData: "कोई उपनाम समूह नहीं मिला।",
            groupName: "समूह का नाम",
            voterCount: "कुल मतदाता :",
            groupId: "समूह आईडी",
            totalGroups: "कुल समूह",
            totalVoters: "कुल मतदाता",
            edit: "एडिट",
            delete: "डिलीट",
            selectAll: "सभी का चयन करें",
            removeAll: "सभी हटाएं",
            updateGroup: "ग्रुप अपडेट करें",
            groupNameLabel: "समूह का नाम",
            updateGroupName: "समूह का नाम अपडेट करें",
            cancel: "रद्द करें",
            update: "अपडेट करें"
        },
        gu: {
            pageTitle: "મારા ઉપનામ જૂથો",
            filterGroups: "જૂથ નામથી શોધો...",
            loadingData: "ઉપનામ જૂથો લોડ થઈ રહ્યા છે...",
            noData: "કોઈ ઉપનામ જૂથ મળ્યા નથી.",
            groupName: "જૂથ નામ",
            voterCount: "કુલ મતદાર :",
            groupId: "જૂથ ID",
            totalGroups: "કુલ જૂથો",
            totalVoters: "કુલ મતદારો",
            edit: "એડિટ",
            delete: "ડિલીટ",
            selectAll: "બધા પસંદ કરો",
            removeAll: "બધા કાઢો",
            updateGroup: "ગૃપ અપડેટ કરો",
            groupNameLabel: "જૂથ નામ",
            updateGroupName: "જૂથ નામ અપડેટ કરો",
            cancel: "રદ કરો",
            update: "અપડેટ કરો"
        }
    };

    const txt = t[lang] || t.gu;


    const passedUserId = location.state?.userId;
    const targetUserId = passedUserId || userId;

    const userName = location.state?.userName;

    // --- Fetch Surname Groups API ---
    const fetchSurnameGroups = async () => {
        if (!appId || !userId) return;

        setLoading(true);
        setSearchTerm("");
        setCurrentPage(1);

        try {
            const response = await axios.post(`${BASE_URL}/api/dis_my_surname_group`, {
                app_id: appId,
                user_id: targetUserId
            });

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setSurnameGroups(response.data.data.result);
            } else {
                setSurnameGroups([]);
            }
        } catch (error) {
            console.error("Error fetching surname groups:", error);
            setSurnameGroups([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSurnameGroups();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredGroups = surnameGroups.filter((group) =>
        group.surname && group.surname.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // --- Pagination Logic ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    // Calculate total voters
    const totalVoters = surnameGroups.reduce((sum, group) => sum + (group.voter_count || 0), 0);
    const totalGroups = surnameGroups.length;

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm("Are you sure you want to delete this group?")) return;

        try {
            const response = await axios.post(`${BASE_URL}/api/dlt_surname_group`, {
                app_id: appId,
                user_id: targetUserId,
                group_id: groupId,
                delete_by: userId
            });

            if (response.data.success) {
                alert("Group deleted successfully!");
                fetchSurnameGroups();
            } else {
                alert("Failed to delete group.");
            }
        } catch (error) {
            console.error("Error deleting group:", error);
            alert("An error occurred while deleting.");
        }
    };

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isUpdateNameModalOpen, setIsUpdateNameModalOpen] = useState(false);
    const [editMembers, setEditMembers] = useState([]);
    const [currentGroupId, setCurrentGroupId] = useState(null);
    const [currentGroupName, setCurrentGroupName] = useState("");
    const [newGroupName, setNewGroupName] = useState("");
    const [updating, setUpdating] = useState(false);

    // Helper function to add trailing comma
    const addTrailingComma = (str) => {
        if (!str) return '';
        return str.endsWith(',') ? str : str + ',';
    };

    // Edit button function - First popup (select voters)
    const handleEditGroup = async (groupId, groupName) => {
        setCurrentGroupId(groupId);
        setCurrentGroupName(groupName);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_surname_group_edit`, {
                app_id: appId,
                user_id: targetUserId,
                group_id: groupId
            });
            if (response.data.success) {
                const members = response.data.data.result || [];
                // Add a checked property to each member based on is_member
                const membersWithCheck = members.map(member => ({
                    ...member,
                    checked: member.is_member === 1
                }));
                setEditMembers(membersWithCheck);
                setIsEditModalOpen(true);
            }
        } catch (error) {
            console.error("Error fetching edit data:", error);
        }
    };

    // Handle checkbox change
    const handleCheckboxChange = (index) => {
        const updatedMembers = [...editMembers];
        updatedMembers[index].checked = !updatedMembers[index].checked;
        setEditMembers(updatedMembers);
    };

    // Select All
    const handleSelectAll = () => {
        const updatedMembers = editMembers.map(member => ({
            ...member,
            checked: true
        }));
        setEditMembers(updatedMembers);
    };

    // Remove All (Uncheck All)
    const handleRemoveAll = () => {
        const updatedMembers = editMembers.map(member => ({
            ...member,
            checked: false
        }));
        setEditMembers(updatedMembers);
    };

    // After selecting voters, open second popup for group name update
    const handleProceedToUpdateName = () => {
        setNewGroupName(currentGroupName);
        setIsEditModalOpen(false);
        setIsUpdateNameModalOpen(true);
    };

    // Final Update with API - with distinct surnames and trailing comma
    const handleFinalUpdate = async () => {
        const selectedMembers = editMembers.filter(member => member.checked);

        // Get distinct surnames from selected members
        const distinctSurnames = [...new Set(selectedMembers.map(member => member.eng_surname || '').filter(s => s))];

        // Get idcard list (can have duplicates as per requirement)
        let idcardList = selectedMembers.map(member => member.idcard_no).join(',');
        idcardList = addTrailingComma(idcardList);

        // Create distinct surname list with trailing comma
        let surnameList = distinctSurnames.join(',');
        surnameList = addTrailingComma(surnameList);

        setUpdating(true);
        try {
            console.log(targetUserId, currentGroupId, newGroupName, surnameList, idcardList);
            const response = await axios.post(`${BASE_URL}/api/upd_surname_group`, {
                app_id: appId,
                user_id: targetUserId,
                group_id: currentGroupId,
                group_name: newGroupName,
                seed_surname: newGroupName,
                surname_list: surnameList,
                idcard_list: idcardList,
                modify_by: userId
            });

            if (response.data.success) {
                alert("Group updated successfully!");
                setIsUpdateNameModalOpen(false);
                fetchSurnameGroups();
            } else {
                alert("Failed to update group.");
            }
        } catch (error) {
            console.error("Error updating group:", error);
            alert("An error occurred while updating.");
        } finally {
            setUpdating(false);
        }
    };

    // Calculate selected count
    const selectedCount = editMembers.filter(m => m.checked).length;
    const totalCount = editMembers.length;

    return (
        <div className="surname-group-page">
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                {/* Stats Cards */}
                <div className="stats-container mb-4 slide-up-element" style={{ animationDelay: '0.05s' }}>
                    <div className="stat-card total-groups-card">
                        
                        <div className="stat-info">
                            <span className="stat-label">{txt.totalGroups}</span>
                            <span className="stat-value">{totalGroups}</span>
                        </div>
                    </div>
                    <div className="stat-card total-voters-card">
                        
                        <div className="stat-info">
                            <span className="stat-label">{txt.totalVoters}</span>
                            <span className="stat-value">{totalVoters.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* Search Bar & Insert Button Container */}
                <div className="filter-container mb-4 slide-up-element">
                    <div className="search-wrapper">
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.filterGroups}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchTerm && (
                            <button className="close-search-btn" onClick={() => setSearchTerm('')}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>


                </div>

                {/* Content */}
                {loading ? (
                    <div className="loader-wrapper text-center p-5">
                        <div className="spinner"></div>
                        <p className="mt-3 text-muted">{txt.loadingData}</p>
                    </div>
                ) : currentItems.length > 0 ? (
                    <>
                        <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                            {currentItems.map((group, index) => (
                                <div className="col-12 col-md-4 col-lg-4" key={group.group_id || index}>

                                    <div className="modern-sg-card" onClick={() => navigate('/dis_surname_group_wise_voter', {
                                        state: {
                                            user_id: targetUserId,
                                            group_id: group.group_id
                                        }
                                    })}>

                                        <div className="card-top-section">
                                            <div className="d-flex align-items-center">
                                                <div className="sg-serial-circle">
                                                    {indexOfFirstItem + index + 1}
                                                </div>
                                                <h3 className="sg-group-name">{group.surname}</h3>
                                            </div>

                                            <div className="sg-voter-pill">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                                                </svg>
                                                {txt.voterCount} {group.voter_count}
                                            </div>
                                        </div>



                                    </div>

                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {filteredGroups.length > itemsPerPage && (
                            <div className="pagination-wrapper">
                                <div className="pagination-content">
                                    <button
                                        className="pagination-nav-btn"
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>

                                    <div className="page-numbers">
                                        {getPageNumbers().map((number, index) => (
                                            <React.Fragment key={index}>
                                                {number === '...' ? (
                                                    <span className="page-dots">...</span>
                                                ) : (
                                                    <button
                                                        className={`page-num-btn ${currentPage === number ? 'active' : ''}`}
                                                        onClick={() => paginate(number)}
                                                    >
                                                        {number}
                                                    </button>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>

                                    <button
                                        className="pagination-nav-btn"
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center text-muted p-5 bg-white rounded shadow-sm border slide-up-element">
                        <div style={{ fontSize: '40px', marginBottom: '16px' }}>📁</div>
                        <h4>{txt.noData}</h4>
                    </div>
                )}

                {/* First Modal - Select Voters */}
                {isEditModalOpen && (
                    <div className="modal-overlay">
                        <div className="edit-modal-content">
                            <div className="edit-modal-header">
                                <div className="modal-selection-info">
                                    <span className="selection-count">પસંદ : {selectedCount} / {totalCount}</span>
                                    <div className="selection-actions">
                                        <button className="selection-action-btn select-all-btn" onClick={handleSelectAll}>
                                            {txt.selectAll}
                                        </button>
                                        <span className="action-separator">|</span>
                                        <button className="selection-action-btn remove-all-btn" onClick={handleRemoveAll}>
                                            {txt.removeAll}
                                        </button>
                                    </div>
                                </div>
                                <button className="modal-close-btn" onClick={() => setIsEditModalOpen(false)}>×</button>
                            </div>

                            <div className="edit-modal-body">
                                {editMembers.map((member, idx) => (
                                    <div key={member.voter_id || idx} className="edit-member-row">
                                        <div className="member-serial">{idx + 1}</div>
                                        <div className="member-info">
                                            <div className="member-name">
                                                {member.eng_f_name} {member.eng_surname}
                                            </div>
                                            <div className="member-details">
                                                <span className="member-detail-item">ક્મીસ નં. : {member.slnoinpart || '-'}</span>
                                                <span className="member-detail-separator">|</span>
                                                <span className="member-detail-item">બુધ : {member.part_no || '-'}</span>
                                            </div>
                                            <div className="member-idcard">{member.idcard_no || '-'}</div>
                                        </div>
                                        <div className="member-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={member.checked}
                                                onChange={() => handleCheckboxChange(idx)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="edit-modal-footer">
                                <button
                                    className="update-group-btn"
                                    onClick={handleProceedToUpdateName}
                                >
                                    {txt.updateGroup}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Second Modal - Update Group Name */}
                {isUpdateNameModalOpen && (
                    <div className="modal-overlay">
                        <div className="update-name-modal-content">
                            <div className="update-name-modal-header">
                                <h3>{txt.updateGroupName}</h3>
                                <button className="modal-close-btn" onClick={() => setIsUpdateNameModalOpen(false)}>×</button>
                            </div>
                            <div className="update-name-modal-body">
                                <div className="form-group">
                                    <label className="form-label">{txt.groupNameLabel}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newGroupName}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        placeholder="Enter group name"
                                    />
                                </div>
                            </div>
                            <div className="update-name-modal-footer">
                                <button
                                    className="cancel-btn"
                                    onClick={() => setIsUpdateNameModalOpen(false)}
                                >
                                    {txt.cancel}
                                </button>
                                <button
                                    className="submit-update-btn"
                                    onClick={handleFinalUpdate}
                                    disabled={updating || !newGroupName.trim()}
                                >
                                    {updating ? "Updating..." : txt.update}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .surname-group-page {
                    width: 100%;
                    min-height: 100vh;
                    background: #eef2f6;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    padding-bottom: 40px;
                }
                .header-wrapper {
                    background: white;
                    padding: 0 24px;
                    position: sticky;
                    top: 0;
                    z-index: 50;
                }

                /* Stats Cards */
                .stats-container {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                .stat-card {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    background: white;
                    padding: 20px 24px;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
                }
                .total-groups-card { border-left: 4px solid #3b82f6; }
                .total-voters-card { border-left: 4px solid #10b981; }
                .stat-icon { font-size: 36px; }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
                .stat-value { font-size: 32px; font-weight: 800; color: #0f172a; line-height: 1.2; }
                .total-groups-card .stat-value { color: #3b82f6; }
                .total-voters-card .stat-value { color: #10b981; }

                /* Search Bar */
                .search-wrapper { position: relative; max-width: 450px; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 14px 40px 14px 46px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
                .close-search-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: #f1f5f9; border: none; border-radius: 50%; padding: 4px; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .close-search-btn:hover { background: #e2e8f0; color: #1e293b; }

                /* Modern Card CSS */
                .modern-sg-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #cbd5e1;
                    overflow: hidden;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    cursor: pointer;
                }
                .modern-sg-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 16px -4px rgba(0,0,0,0.1);
                    border-color: #94a3b8;
                }

                .card-top-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    background: white;
                }
                .sg-serial-circle {
                    width: 42px;
                    height: 42px;
                    background-color: #3b426e;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.1rem;
                    font-weight: 800;
                    margin-right: 16px;
                    flex-shrink: 0;
                }
                .sg-group-name {
                    margin: 0;
                    font-size: 1.2rem;
                    font-weight: 800;
                    color: #1e293b;
                    word-break: break-word;
                }
                .sg-voter-pill {
                    background-color: #eef2ff;
                    color: #1e3a8a;
                    font-weight: 700;
                    font-size: 13px;
                    padding: 6px 14px;
                    border-radius: 20px;
                    border: 1px solid #c7d2fe;
                    display: flex;
                    align-items: center;
                    white-space: nowrap;
                }

                .card-action-section {
                    display: flex;
                    align-items: center;
                    border-top: 1px solid #e2e8f0;
                    background: #ffffff;
                }
                .sg-action-btn {
                    flex: 1;
                    background: transparent;
                    border: none;
                    padding: 14px 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }
                .sg-action-btn:hover {
                    background: #f8fafc;
                }
                .edit-btn { color: #3b426e; }
                .delete-btn { color: #e11d48; }
                .action-divider {
                    width: 1px;
                    height: 30px;
                    background-color: #e2e8f0;
                }

                /* Loader */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                /* Pagination */
                .pagination-wrapper { display: flex; justify-content: center; padding: 30px 0 10px 0; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
                .pagination-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: transparent; color: #1e3a8a; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .pagination-nav-btn:hover:not(:disabled) { background-color: #eff6ff; }
                .pagination-nav-btn:disabled { color: #cbd5e1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 4px; align-items: center; }
                .page-num-btn { min-width: 32px; height: 32px; padding: 0 6px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer; transition: all 0.2s; }
                .page-num-btn:hover { color: #1e3a8a; background-color: #f8fafc; }
                .page-num-btn.active { background-color: #1e3a8a; color: white; box-shadow: 0 2px 4px rgba(30, 58, 138, 0.3); }
                .page-dots { color: #94A3B8; font-weight: bold; padding: 0 4px; }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Filter Container */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                }
                .btn-insert-group {
                    background-color: #3b82f6;
                    color: white;
                    padding: 12px 20px;
                    border: none;
                    border-radius: 10px;
                    font-weight: 600;
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background 0.2s;
                }
                .btn-insert-group:hover {
                    background-color: #2563eb;
                }

                /* First Modal - Edit Voters */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                .edit-modal-content {
                    background: white;
                    width: 90%;
                    max-width: 500px;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.3);
                    animation: modalSlideIn 0.3s ease;
                }
                @keyframes modalSlideIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                .edit-modal-header {
                    padding: 16px 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-selection-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .selection-count {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1e293b;
                    background: #e2e8f0;
                    padding: 4px 12px;
                    border-radius: 20px;
                }
                .selection-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .selection-action-btn {
                    background: none;
                    border: none;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    padding: 4px 8px;
                    border-radius: 6px;
                    transition: background 0.2s;
                }
                .select-all-btn {
                    color: #3b82f6;
                }
                .select-all-btn:hover {
                    background: #eff6ff;
                }
                .remove-all-btn {
                    color: #ef4444;
                }
                .remove-all-btn:hover {
                    background: #fef2f2;
                }
                .action-separator {
                    color: #cbd5e1;
                    font-size: 14px;
                }
                .modal-close-btn {
                    background: #f1f5f9;
                    border: none;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    font-size: 24px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    transition: all 0.2s;
                }
                .modal-close-btn:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .edit-modal-body {
                    max-height: 450px;
                    overflow-y: auto;
                    padding: 8px 0;
                }
                .edit-member-row {
                    display: flex;
                    align-items: center;
                    padding: 14px 20px;
                    border-bottom: 1px solid #f1f5f9;
                    transition: background 0.2s;
                }
                .edit-member-row:hover {
                    background: #fafcff;
                }
                .member-serial {
                    width: 32px;
                    font-size: 14px;
                    font-weight: 700;
                    color: #64748b;
                    flex-shrink: 0;
                }
                .member-info {
                    flex: 1;
                    padding-left: 8px;
                }
                .member-name {
                    font-size: 15px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 4px;
                }
                .member-details {
                    font-size: 11px;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 3px;
                    flex-wrap: wrap;
                }
                .member-detail-separator {
                    color: #cbd5e1;
                }
                .member-idcard {
                    font-size: 11px;
                    color: #3b82f6;
                    font-weight: 500;
                    font-family: monospace;
                }
                .member-checkbox {
                    flex-shrink: 0;
                    margin-left: 12px;
                }
                .member-checkbox input[type="checkbox"] {
                    width: 20px;
                    height: 20px;
                    cursor: pointer;
                    accent-color: #3b82f6;
                }
                .edit-modal-footer {
                    padding: 16px 20px;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                }
                .update-group-btn {
                    width: 100%;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .update-group-btn:hover {
                    background: #2563eb;
                }

                /* Second Modal - Update Group Name */
                .update-name-modal-content {
                    background: white;
                    width: 90%;
                    max-width: 400px;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 20px 35px -10px rgba(0, 0, 0, 0.3);
                    animation: modalSlideIn 0.3s ease;
                }
                .update-name-modal-header {
                    padding: 20px 24px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .update-name-modal-header h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                }
                .update-name-modal-body {
                    padding: 24px;
                }
                .form-group {
                    margin-bottom: 0;
                }
                .form-label {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 8px;
                }
                .form-input {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #cbd5e1;
                    border-radius: 12px;
                    font-size: 15px;
                    color: #1e293b;
                    outline: none;
                    transition: all 0.2s ease;
                }
                .form-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .update-name-modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #e2e8f0;
                    background: #f8fafc;
                    display: flex;
                    gap: 12px;
                }
                .cancel-btn {
                    flex: 1;
                    background: #f1f5f9;
                    color: #64748b;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .cancel-btn:hover {
                    background: #e2e8f0;
                }
                .submit-update-btn {
                    flex: 1;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .submit-update-btn:hover {
                    background: #2563eb;
                }
                .submit-update-btn:disabled {
                    background: #94a3b8;
                    cursor: not-allowed;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .stats-container { flex-direction: column; gap: 12px; }
                    .stat-card { padding: 16px 20px; }
                    .stat-value { font-size: 28px; }
                    .stat-icon { font-size: 32px; }
                }
                @media (max-width: 640px) {
                    .filter-container {
                        flex-direction: column;
                    }
                    .btn-insert-group {
                        width: 100%;
                    }
                    .card-top-section {
                        padding: 16px;
                    }
                    .sg-serial-circle {
                        width: 36px;
                        height: 36px;
                        font-size: 1rem;
                        margin-right: 12px;
                    }
                    .sg-group-name {
                        font-size: 1.05rem;
                    }
                    .sg-voter-pill {
                        font-size: 11px;
                        padding: 4px 10px;
                    }
                    .sg-action-btn {
                        padding: 12px 0;
                        font-size: 14px;
                    }
                    .edit-modal-content {
                        width: 95%;
                        max-width: 95%;
                    }
                    .member-serial {
                        width: 28px;
                        font-size: 12px;
                    }
                    .member-name {
                        font-size: 13px;
                    }
                    .member-details {
                        font-size: 10px;
                    }
                    .update-name-modal-content {
                        width: 95%;
                    }
                        /* Stats Cards in One Row */
                    .stats-container { 
                        flex-direction: row; /* Hamesha row ma rahese */
                        gap: 12px; 
                    }
                    .stat-card { 
                        padding: 12px; 
                        gap: 12px; 
                    }
                    .stat-value { font-size: 22px; }
                    .stat-icon { font-size: 26px; }
                    .stat-label { font-size: 10px; }
                }
            `}</style>
        </div>
    );
};

export default VolSurnameGroup;